/**
 * LOT CONFLICT: THE SOURCES DISAGREE ABOUT THE LAND (2026-11-03, owner spec; aligned to #120).
 *
 * When the sources state lot sizes that differ by more than 2%, the product used to pick one
 * without telling anybody. The lot adjustment is priced from this figure, so a silent pick is a
 * silent change to the value. The owner's rule: show every figure with its source tag and record,
 * and let the agent pick one. No silent choice, and never a zero.
 *
 * THE CONTRACT (server: src/server/lot_candidates.py, PR #120). The subject payload carries
 *
 *     lot_candidates: [{ value_sqft, source, record_id, computed_note }]     (always present, maybe [])
 *
 *   value_sqft     square feet, a positive finite number (zero, negative and missing are omitted
 *                  server-side, never sent as 0)
 *   source         the tag exactly as it prints: "Collin CAD land area (sq ft)", "Collin CAD acreage",
 *                  "NTREIS MLS LotSizeSquareFeet", "NTREIS MLS LotSizeAcres", and any source added later
 *   record_id      the record the figure was read from (parcel id, MLS number)
 *   computed_note  the server's own sentence when the figure was converted or derived, rendered
 *                  VERBATIM: "County acreage 0.2300 ac x 43,560 = 10,019 sq ft",
 *                  "County area field is 0; frontage x depth 77 x 130 = 10,010 sq ft". null when stated.
 *
 * The list is for the agent to look at; it never selects. The server's own cascade still fills
 * `lot_sf` independently. Fewer than two usable candidates: NOTHING here applies, the grid is built
 * exactly as it always was, and a lone candidate is never auto-filled. This module never invents a
 * candidate, averages two, or collapses equal figures from different sources.
 *
 * THE DIFFERENCE RULE, stated once and shown to the agent verbatim (the (i) beside the picker):
 *
 *     (max − min) / max > 0.02       over ALL candidates; exactly 2% is NOT a conflict
 *
 * Any two candidates differing by more than 2% means the widest pair does, so comparing the largest
 * with the smallest is the whole test. In conflict, EVERY candidate is listed, including those that
 * agree with each other. Within 2% there is no picker and today's value is shown.
 */
export interface LotCandidate {
  value_sqft: number;
  source: string;
  record_id: string | null;
  computed_note: string | null;
}

export const LOT_CONFLICT_RULE = "(max − min) / max > 0.02";

export const LOT_CONFLICT_EXPLAIN =
  "Each source we read states its own lot size. They count as different when the largest and the " +
  `smallest figure satisfy ${LOT_CONFLICT_RULE}, that is, when the gap is more than 2% of the ` +
  "largest; exactly 2% is not a difference. When they differ we do not choose for you and we do not " +
  "average them: every figure is listed with its source and record, so pick the one you trust, or " +
  "type your own. Your pick is tagged with its source and record.";

/** Parse the contract defensively. Entries that cannot be trusted as a lot size (not a positive
 * finite number, no source) are dropped, never repaired. Returns [] for anything that is not the
 * contract, so every caller can treat "fewer than two" as "behave as before". */
export function parseLotCandidates(raw: unknown): LotCandidate[] {
  if (!Array.isArray(raw)) return [];
  const out: LotCandidate[] = [];
  for (const e of raw) {
    if (!e || typeof e !== "object") continue;
    const r = e as Record<string, unknown>;
    const v = typeof r.value_sqft === "string" && r.value_sqft.trim() !== "" ? Number(r.value_sqft) : r.value_sqft;
    const source = typeof r.source === "string" ? r.source.trim() : "";
    if (typeof v !== "number" || !Number.isFinite(v) || v <= 0 || !source) continue;
    const id = r.record_id;
    const note = typeof r.computed_note === "string" && r.computed_note.trim() ? r.computed_note.trim() : null;
    out.push({
      value_sqft: v,
      source,
      record_id: typeof id === "string" || typeof id === "number" ? String(id).trim() || null : null,
      computed_note: note,
    });
  }
  return out;
}

/** The gap between two lot sizes as a fraction of the larger. */
export function lotGap(a: number, b: number): number {
  const hi = Math.max(a, b);
  return hi > 0 ? Math.abs(a - b) / hi : 0;
}

/** True when two figures differ by MORE than 2% of the larger. Compared as `diff * 50 > max` so
 * that exactly 2% is exact in floating point and is not a conflict. */
export function lotsConflict(a: number, b: number): boolean {
  return Math.abs(a - b) * 50 > Math.max(a, b);
}

/** The candidates when (and only when) they conflict; otherwise null. */
export function conflictingLotCandidates(raw: unknown): LotCandidate[] | null {
  const cands = parseLotCandidates(raw);
  if (cands.length < 2) return null;
  const values = cands.map((c) => c.value_sqft);
  return lotsConflict(Math.min(...values), Math.max(...values)) ? cands : null;
}

/** The gap of a conflicting set, rounded to a whole percent for the sentence (display only). */
export function conflictPercent(cands: LotCandidate[]): number {
  const values = cands.map((c) => c.value_sqft);
  return Math.round(lotGap(Math.min(...values), Math.max(...values)) * 100);
}

export function candidateLabel(c: LotCandidate): string {
  return `${Math.round(c.value_sqft).toLocaleString("en-US")} sq ft`;
}

/** The words that stand where the source tag would be while the lot is unresolved. */
export const LOT_UNRESOLVED_WORDS = "— Lot sources differ; pick one below";

/** One edit the agent made that a later pick replaced. Kept, never dropped: the override trail is
 * the record of what the agent did, and a pick does not unmake it. */
export interface SupersededEdit {
  editedValue: number | string | boolean | null;
  editedSource: string;
  fromValue: number | string | boolean | null;
  fromSource: string | null;
  supersededByValue: number;
  supersededBySource: string;
}

export interface SubjectOverrideEntry {
  original: unknown;
  source: string | null;
  superseded?: SupersededEdit[];
}

/** The `_overrides.subject[key]` entry for a grid row, or null when the row carries no override.
 * An edited row sends exactly what it always did; a row whose edit was later superseded by a pick
 * also sends the superseded list (and `original: null`, since the live value is a source's). */
export function subjectOverrideFor(field: {
  edited?: boolean; originalValue?: unknown; sourceTag: string | null; supersededEdits?: SupersededEdit[];
}): SubjectOverrideEntry | null {
  const trail = field.supersededEdits;
  if (!field.edited && !(trail && trail.length)) return null;
  const entry: SubjectOverrideEntry = {
    original: field.edited ? (field.originalValue ?? null) : null,
    source: field.sourceTag,
  };
  if (trail && trail.length) entry.superseded = trail;
  return entry;
}

/** Why the lot is blocking, or null. `lotState` is the grid's `lot_sf` row. */
export function lotConflictBlockReason(
  lotState: { candidates?: LotCandidate[]; checked: boolean; value: unknown } | undefined | null,
): string | null {
  const c = lotState?.candidates;
  if (!c || c.length < 2) return null;
  const resolved = lotState!.checked && lotState!.value != null && lotState!.value !== "";
  if (resolved) return null;
  const items = c.map((x) => `${candidateLabel(x)} (${x.source})`);
  const list = items.length > 2 ? `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}` : items.join(" and ");
  return `Lot size is waiting on you: ${list} differ by up to ${conflictPercent(c)}%, more than the 2% we accept. ` +
    "Pick one in the Lot size row, or type your own. We do not choose between them.";
}
